import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { PriorityEnum } from '@application/enums/priority-enum';
import { TaskCompletionStatusEnum } from '@application/enums/task-completion-status-enum';
import { TaskType } from '@application/enums/task-type';
import { AutomaticTaskType } from '@application/enums/automatic-task-type';
import { getAllPeopleForTasksAsync } from '@application/features/people/peopleThunk';
import { deleteTaskAsync, updateTaskAsync } from '@application/features/task/taskThunk';
import { CollaboratorTaskAccessLevel } from '@application/enums/collaborator-task-access-level';
import { getCollaboratorsByEventAsync } from '@application/features/collaborator/collaboratorThunk';
import { hasPermission } from '@application/helpers/permission-helper';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { Icon } from '@iconify/react';
import { alpha } from '@mui/material';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import TaskViewDialog from './ViewTaskDialog';
import { Avatar, Box, Button, Checkbox, Chip, CircularProgress, Drawer, IconButton, TextField, Typography, useTheme } from '@mui/material';
import CustomDialog from '@ui/components/common/Dialog';
import { StyledMenu } from '@ui/components/common/Menu';
import AddNewTask from '@ui/pages/Tasks/AddNewTask';
import AddNewTaskDialog from '@ui/pages/Tasks/AddNewTask/addNewTaskDialog';
import { centeredRowStack } from '@ui/style/style';
import { useResponsive } from '@ui/style/useResponsive';
import { ArrowRight, Calendar, MagicStar, Task, UserOctagon, UserTick } from 'iconsax-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Delete } from 'react-iconly';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import useTaskCardStyles from './useTaskCardStyles';
import { APIStatus } from '@application/enums/api-status';
import dayjs from 'dayjs';
/** Replaces {0}, {1}, ... in str with params[0], params[1], ... */
function formatWithParams(str, params) {
    return params.reduce((s, p, i) => s.replace(new RegExp('\\{' + i + '\\}', 'g'), String(p)), str);
}
const TaskCard = ({ heading, status, eventOrganizer, date, time, priority = 'medium', // Default to medium if not provided
taskData, onStatusUpdate, taskStatus = false, hideMenu = false, showRating = true, userAssignedDisabled, taskCardSx, assignButtonSx, taskType, personId, relationId, showPersonIcon = false, boxShadow = true, disableClick = false, highlighted = false }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const styles = useTaskCardStyles(priority, status, boxShadow, highlighted);
    const isAutomaticTask = taskData &&
        taskData.automaticTaskType !== undefined &&
        taskData.automaticTaskType !== AutomaticTaskType.None;
    let params = [];
    if (isAutomaticTask && taskData?.actionData) {
        try {
            const parsed = JSON.parse(taskData.actionData);
            if (parsed && Array.isArray(parsed.p)) {
                params = parsed.p;
            }
        }
        catch (e) {
            console.error('Failed to parse task actionData:', e);
        }
    }
    const translatedHeading = isAutomaticTask && taskData
        ? (params.length ? formatWithParams(t(taskData.name), params) : t(taskData.name))
        : heading;
    const dispatch = useAppDispatch();
    const userState = useAppSelector((state) => state.user);
    const peopleState = useAppSelector((state) => state.people);
    const collaboratorState = useAppSelector((state) => state.collaborator);
    const [anchorUsersEl, setAnchorUsersEl] = useState(null);
    const [anchorCollaboratorsEl, setanchorCollaboratorsEl] = useState(null);
    const [anchorMoreOptionEl, setAnchorMoreOptionEl] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedCollaborators, setSelectedCollaborators] = useState([]);
    const [userSearchTerm, setUserSearchTerm] = useState('');
    dayjs.extend(customParseFormat);
    // Helper function to get priority translation key
    const getPriorityTranslationKey = (priorityValue) => {
        switch (priorityValue) {
            case PriorityEnum.High:
                return 'priority.high';
            case PriorityEnum.Medium:
                return 'priority.medium';
            case PriorityEnum.Low:
                return 'priority.low';
            case PriorityEnum.None:
                return 'priority.none';
            default:
                return 'priority.medium';
        }
    };
    // Helper function to get status translation and color
    const getStatusInfo = () => {
        const defaultStatus = TaskCompletionStatusEnum.New;
        const currentStatus = status ?? defaultStatus;
        switch (currentStatus) {
            case TaskCompletionStatusEnum.New:
                return {
                    label: t('tasks.new', { defaultValue: 'New' }),
                    circleColor: theme.palette.text.primary,
                    textColor: theme.palette.text.primary
                };
            case TaskCompletionStatusEnum.InProgress:
                return {
                    label: t('tasks.inProgress', { defaultValue: 'In progress' }),
                    circleColor: theme.palette.primary.main,
                    textColor: theme.palette.primary.main
                };
            case TaskCompletionStatusEnum.Completed:
                return {
                    label: t('tasks.completed', { defaultValue: 'Completed' }),
                    circleColor: theme.palette.success.main,
                    textColor: theme.palette.success.main
                };
            default:
                return {
                    label: t('tasks.new', { defaultValue: 'New' }),
                    circleColor: theme.palette.text.primary,
                    textColor: theme.palette.text.primary
                };
        }
    };
    const loggedInUserPermissions = useSelector((state) => state.auth.currentUser?.permissions || []);
    const canCreateTasks = hasPermission(loggedInUserPermissions, 'Permissions.VdkTasks.Create');
    const canUpdateTasks = hasPermission(loggedInUserPermissions, 'Permissions.VdkTasks.Update');
    const canDeleteTasks = hasPermission(loggedInUserPermissions, 'Permissions.VdkTasks.Delete');
    const currentUser = useSelector((state) => state.auth.currentUser);
    const isCollaborator = Array.isArray(currentUser?.role) &&
        (currentUser?.role.includes('Collaborator') || currentUser?.role.includes('TransportManager'));
    // Intialize the Collaborators
    React.useEffect(() => {
        if (taskData?.eventId && taskData?.collaboratorTaskAccessLevel) {
            dispatch(getCollaboratorsByEventAsync({
                eventId: taskData?.eventId,
                accessLevel: taskData?.collaboratorTaskAccessLevel
            }));
        }
    }, [dispatch, taskData?.eventId, taskData?.collaboratorTaskAccessLevel]);
    // Initialize selectedUsers with existing assigned users
    React.useEffect(() => {
        if (taskData?.assignedTo) {
            setSelectedUsers(taskData.assignedTo);
        }
        else {
            setSelectedUsers([]);
        }
    }, [taskData?.assignedTo]);
    // Close menu when switching between button and avatar states to prevent positioning issues
    const prevSelectedUsersLength = React.useRef(selectedUsers.length);
    React.useEffect(() => {
        const wasEmpty = prevSelectedUsersLength.current === 0;
        const isEmpty = selectedUsers.length === 0;
        // Close menu when transitioning between empty and non-empty states
        // This prevents menu from jumping to top when DOM element changes
        if (wasEmpty !== isEmpty && anchorUsersEl) {
            setAnchorUsersEl(null);
        }
        prevSelectedUsersLength.current = selectedUsers.length;
    }, [selectedUsers.length]);
    // Initialize selectedCollaborators with existing assigned collaborators
    React.useEffect(() => {
        if (taskData?.collaboratorIds) {
            setSelectedCollaborators(taskData.collaboratorIds);
        }
        else {
            setSelectedCollaborators([]);
        }
    }, [taskData?.collaboratorIds]);
    // Ensure we have people data when rendering People Activity label
    React.useEffect(() => {
        if (taskType === TaskType.People_Activity && !peopleState?.peopleForTasks) {
            dispatch(getAllPeopleForTasksAsync());
        }
    }, [dispatch, taskType, peopleState?.peopleForTasks]);
    const getPeopleActivityLabel = () => {
        if (taskType !== TaskType.People_Activity)
            return '';
        // Prefer explicit props, fall back to taskData if not provided
        const basePersonId = personId || taskData?.personId;
        const baseRelationId = relationId || taskData?.relationId;
        const people = peopleState?.peopleForTasks;
        if (!people || !basePersonId)
            return 'People Activity';
        const person = people.find((p) => p.id === basePersonId);
        if (!person)
            return 'People Activity';
        if (baseRelationId) {
            const family = (person.familyMembers || []).find((fm) => fm.id === baseRelationId);
            if (family) {
                const relationText = family.relation ? ` (${family.relation} of ${person.name})` : '';
                return `${family.name}${relationText}`;
            }
        }
        return person.name;
    };
    // Helper function to check if collaborators should be shown
    const shouldShowCollaborators = () => {
        if (!taskData)
            return false;
        // Must have access level set
        if (taskData.collaboratorTaskAccessLevel === undefined ||
            taskData.collaboratorTaskAccessLevel === null ||
            (taskData.collaboratorTaskAccessLevel !== 0 && taskData.collaboratorTaskAccessLevel !== 1)) {
            return false;
        }
        // Must be an Event task with eventId
        return taskData.taskType === TaskType.Event && !!taskData.eventId;
    };
    // Handler for opening collaborators menu
    const handleCollaboratorsMenuOpen = async (event) => {
        event.stopPropagation();
        setanchorCollaboratorsEl(event.currentTarget);
        // Only fetch if we have the required data
        if (shouldShowCollaborators() && taskData) {
            try {
                await dispatch(getCollaboratorsByEventAsync({
                    eventId: taskData.eventId,
                    accessLevel: taskData.collaboratorTaskAccessLevel
                })).unwrap();
            }
            catch (error) {
                console.error('Failed to fetch collaborators:', error);
            }
        }
    };
    // Handler for collaborator selection
    const handleCollaboratorSelection = async (collaboratorId) => {
        if (!taskData || isUpdating || !shouldShowCollaborators() || isCollaborator)
            return;
        const newSelectedCollaborators = selectedCollaborators.includes(collaboratorId)
            ? selectedCollaborators.filter((id) => id !== collaboratorId)
            : [...selectedCollaborators, collaboratorId];
        setSelectedCollaborators(newSelectedCollaborators);
        setIsUpdating(true);
        try {
            const updateRequest = {
                id: taskData.id,
                name: taskData.name,
                deadline: taskData.deadline,
                priority: taskData.priority,
                completionStatus: taskData.completionStatus,
                assignedTo: taskData?.assignedTo || null,
                eventId: taskData?.eventId || null,
                isActivityTask: taskData?.isActivityTask,
                activityId: taskData?.activityId,
                relationId: taskData?.relationId,
                personId: taskData?.personId,
                taskType: taskData?.taskType,
                collaboratorIds: newSelectedCollaborators.length > 0 ? newSelectedCollaborators : null,
                CollaboratorTaskAccessLevel: taskData?.collaboratorTaskAccessLevel || undefined
            };
            await dispatch(updateTaskAsync(updateRequest)).unwrap();
            if (onStatusUpdate) {
                onStatusUpdate();
            }
            const collaborator = collaboratorState.eventCollaborators?.find((c) => c.id === collaboratorId);
            const collaboratorName = collaborator
                ? `${collaborator.firstName} ${collaborator.lastName}`
                : 'Unknown';
            if (newSelectedCollaborators.includes(collaboratorId)) {
                toast.success(t('tasks.collaboratorAssignedSuccessfully', { collaboratorName }));
            }
            else {
                toast.success(t('tasks.collaboratorUnassignedSuccessfully', { collaboratorName }));
            }
        }
        catch (error) {
            // Revert on error
            setSelectedCollaborators(selectedCollaborators);
        }
        finally {
            setIsUpdating(false);
        }
    };
    const handleUserSelection = async (userId) => {
        if (!taskData || isUpdating)
            return;
        const newSelectedUsers = selectedUsers.includes(userId)
            ? selectedUsers.filter((id) => id !== userId)
            : [...selectedUsers, userId];
        // Close menu if transitioning between empty and non-empty states
        // This prevents menu positioning issues when DOM element changes
        const wasEmpty = selectedUsers.length === 0;
        const willBeEmpty = newSelectedUsers.length === 0;
        if (wasEmpty !== willBeEmpty && anchorUsersEl) {
            setAnchorUsersEl(null);
        }
        setSelectedUsers(newSelectedUsers);
        // Auto-save the changes
        setIsUpdating(true);
        try {
            const updateRequest = {
                id: taskData.id,
                name: taskData.name,
                deadline: taskData.deadline,
                priority: taskData.priority,
                completionStatus: taskData.completionStatus,
                assignedTo: newSelectedUsers.length > 0 ? newSelectedUsers : null,
                eventId: taskData?.eventId || null,
                isActivityTask: taskData?.isActivityTask,
                activityId: taskData?.activityId,
                relationId: taskData?.relationId,
                personId: taskData?.personId,
                taskType: taskData?.taskType,
                collaboratorIds: taskData?.collaboratorIds || null,
                CollaboratorTaskAccessLevel: taskData?.collaboratorTaskAccessLevel || undefined
            };
            await dispatch(updateTaskAsync(updateRequest)).unwrap();
            if (onStatusUpdate) {
                onStatusUpdate();
            }
            // Show success toast
            const user = userState.usersWithoutPagination.find((u) => u.id === userId);
            const userName = user ? `${user.firstName} ${user.lastName}` : 'Unknown';
            if (newSelectedUsers.includes(userId)) {
                toast.success(t('tasks.taskAssignedSuccessfully', { userName }) ||
                    `Task assigned to ${userName} successfully!`);
            }
            else {
                toast.success(t('tasks.taskUnassignedSuccessfully', { userName }) ||
                    `Task unassigned from ${userName} successfully!`);
            }
        }
        catch (error) {
            // Revert the selection on error
            setSelectedUsers(selectedUsers);
        }
        finally {
            setIsUpdating(false);
            // Keep menu open for multiple selections
        }
    };
    const getStatusActionText = () => {
        switch (status) {
            case TaskCompletionStatusEnum.New:
                return t('tasks.InProgress');
            case TaskCompletionStatusEnum.InProgress:
                return t('tasks.markAsComplete');
            case TaskCompletionStatusEnum.Completed:
                return t('tasks.InProgress');
            default:
                return t('tasks.markAsComplete');
        }
    };
    const getNextCompletionStatus = () => {
        switch (status) {
            case TaskCompletionStatusEnum.New:
                return TaskCompletionStatusEnum.InProgress;
            case TaskCompletionStatusEnum.InProgress:
                return TaskCompletionStatusEnum.Completed;
            case TaskCompletionStatusEnum.Completed:
                return TaskCompletionStatusEnum.InProgress;
            default:
                return TaskCompletionStatusEnum.InProgress;
        }
    };
    const handleStatusUpdate = async () => {
        if (!taskData || isUpdating)
            return;
        setIsUpdating(true);
        try {
            const updateRequest = {
                id: taskData.id,
                name: taskData.name,
                deadline: taskData.deadline,
                priority: taskData.priority,
                completionStatus: getNextCompletionStatus(),
                assignedTo: taskData?.assignedTo || null,
                eventId: taskData?.eventId || null,
                isActivityTask: taskData?.isActivityTask,
                activityId: taskData?.activityId,
                relationId: taskData?.relationId,
                personId: taskData?.personId,
                taskType: taskData?.taskType,
                collaboratorIds: taskData?.collaboratorIds || null,
                CollaboratorTaskAccessLevel: taskData?.collaboratorTaskAccessLevel || undefined
            };
            await dispatch(updateTaskAsync(updateRequest)).unwrap();
            if (onStatusUpdate) {
                onStatusUpdate();
            }
            // Show success toast for status update
            const nextStatus = getNextCompletionStatus();
            let statusMessage = '';
            switch (nextStatus) {
                case TaskCompletionStatusEnum.InProgress:
                    statusMessage = t('tasks.taskMarkedInProgress') || 'Task marked as in progress!';
                    break;
                case TaskCompletionStatusEnum.Completed:
                    statusMessage = t('tasks.taskMarkedCompleted') || 'Task marked as completed!';
                    break;
                default:
                    statusMessage = t('tasks.taskStatusUpdated') || 'Task status updated!';
            }
            toast.success(statusMessage);
        }
        catch (error) {
        }
        finally {
            setIsUpdating(false);
            setAnchorMoreOptionEl(null); // Close the menu
        }
    };
    const handleDeleteTask = async () => {
        if (!taskData || isUpdating)
            return;
        setIsUpdating(true);
        try {
            await dispatch(deleteTaskAsync(taskData.id)).unwrap();
            if (onStatusUpdate) {
                onStatusUpdate();
            }
            // Show success toast for task deletion
            toast.success(t('tasks.taskDeletedSuccessfully') || 'Task deleted successfully!');
        }
        catch (error) {
        }
        finally {
            setIsUpdating(false);
            setAnchorMoreOptionEl(null); // Close the menu
            setOpenDeleteConfirmation(false);
        }
    };
    const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
    const [isComplete, setIsComplete] = useState(false);
    const [isEditTaskOpen, setIsEditTaskOpen] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isViewTaskOpen, setIsViewTaskOpen] = useState(false);
    const { isMobileIpadNavigation } = useResponsive();
    const getAssignedUsers = () => {
        if (!taskData?.assignedTo || taskData.assignedTo.length === 0)
            return [];
        return userState.usersWithoutPagination.filter((user) => taskData.assignedTo?.includes(user.id));
    };
    const assignedUsers = getAssignedUsers();
    const assignedUsersNames = assignedUsers.length > 0
        ? assignedUsers.map((user) => `${user.firstName} ${user.lastName}`).join(', ')
        : '';
    // Helper function to get user initials
    const getUserInitials = (user) => {
        const firstName = user.firstName || '';
        const lastName = user.lastName || '';
        return `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase() || '?';
    };
    const getTaskTypeLabel = () => {
        const type = taskType || taskData?.taskType;
        if (type === TaskType.People_Activity)
            return t('tasks.peopleActivity');
        if (type === TaskType.Event)
            return t('tasks.event');
        return t('tasks.others');
    };
    // Function to get display text for the button
    const getButtonText = () => {
        if (selectedUsers.length === 0) {
            return t('tasks.assignto') || 'Assign To';
        }
        else if (selectedUsers.length === 1) {
            const selectedUser = userState.usersWithoutPagination.find((user) => user.id === selectedUsers[0]);
            return selectedUser ? `${selectedUser.firstName} ${selectedUser.lastName}` : 'Selected';
        }
        else {
            return `${selectedUsers.length} users assigned`;
        }
    };
    const userMenu = [
        ...userState.usersWithoutPagination
            .filter((user) => {
            const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
            return fullName.includes(userSearchTerm.toLowerCase());
        })
            .map((user) => ({
            text: `${user.firstName} ${user.lastName}`,
            to: () => {
                if (isCollaborator)
                    return;
                handleUserSelection(user.id);
            },
            disabled: isCollaborator,
            selected: selectedUsers.includes(user.id),
            group: true,
            keepOpen: true,
            isCheckbox: true,
            icon: (_jsx(Checkbox, { size: 'small', checked: selectedUsers.includes(user.id), onChange: () => {
                    if (isCollaborator)
                        return;
                    handleUserSelection(user.id);
                }, disabled: isCollaborator }))
        }))
    ];
    // Create collaborator menu
    const collaboratorMenu = React.useMemo(() => {
        if (!shouldShowCollaborators())
            return [];
        // While loading, show an inline loader item to avoid global loader usage
        if (collaboratorState.status === APIStatus.PENDING) {
            return [
                {
                    text: '',
                    group: true,
                    disabled: true,
                    icon: _jsx(CircularProgress, { size: 16 })
                }
            ];
        }
        if (!collaboratorState.eventCollaborators ||
            collaboratorState.eventCollaborators.length === 0) {
            return [
                {
                    text: t('common.noData') || 'No collaborators found',
                    group: true,
                    disabled: true
                }
            ];
        }
        const isExternalTask = taskData?.collaboratorTaskAccessLevel === CollaboratorTaskAccessLevel.External;
        const visibleCollaborators = isExternalTask
            ? collaboratorState.eventCollaborators.filter((c) => selectedCollaborators.includes(c.id))
            : collaboratorState.eventCollaborators;
        if (isExternalTask && visibleCollaborators.length === 0) {
            return [
                {
                    text: t('common.noData') || 'No collaborators found',
                    group: true,
                    disabled: true
                }
            ];
        }
        return visibleCollaborators.map((collaborator) => ({
            text: `${collaborator.firstName} ${collaborator.lastName}`,
            to: () => {
                if (isCollaborator || isExternalTask)
                    return;
                handleCollaboratorSelection(collaborator.id);
            },
            selected: selectedCollaborators.includes(collaborator.id),
            group: true,
            keepOpen: true,
            isCheckbox: !isExternalTask,
            disabled: isCollaborator || isExternalTask,
            icon: !isExternalTask ? (_jsx(Checkbox, { size: 'small', checked: selectedCollaborators.includes(collaborator.id), onChange: () => {
                    if (isCollaborator)
                        return;
                    handleCollaboratorSelection(collaborator.id);
                }, disabled: isCollaborator })) : undefined
        }));
    }, [
        collaboratorState.eventCollaborators,
        collaboratorState.status,
        selectedCollaborators,
        taskData?.eventId,
        taskData?.collaboratorTaskAccessLevel,
        taskData?.taskType
    ]);
    const priorityColorMap = {
        high: theme.palette.error.main,
        medium: theme.palette.warning.main,
        low: theme.palette.success.main
    };
    const moreMenuOptions = (() => {
        if (isCollaborator) {
            return [
                {
                    text: getStatusActionText(),
                    to: handleStatusUpdate,
                    disabled: isUpdating,
                    isCheckbox: true,
                    icon: (_jsx(Checkbox, { size: 'small', disabled: isUpdating, onClick: (e) => {
                            e.stopPropagation();
                            handleStatusUpdate();
                        } }))
                }
            ];
        }
        return [
            {
                text: getStatusActionText(),
                to: handleStatusUpdate,
                disabled: isUpdating || (!canUpdateTasks && !canDeleteTasks),
                isCheckbox: true,
                icon: (_jsx(Checkbox, { size: 'small', disabled: isUpdating || (!canUpdateTasks && !canDeleteTasks), onClick: (e) => {
                        e.stopPropagation();
                        handleStatusUpdate();
                    } }))
            },
            ...(status === TaskCompletionStatusEnum.New || status === TaskCompletionStatusEnum.InProgress
                ? [
                    {
                        text: t('tasks.edit'),
                        group: true,
                        icon: _jsx(Icon, { icon: 'eva:edit-outline', fontSize: 20 }),
                        to: () => {
                            if (isMobileIpadNavigation) {
                                setIsDrawerOpen(true);
                            }
                            else {
                                setIsEditTaskOpen(true);
                            }
                        },
                        disabled: !canUpdateTasks && !canDeleteTasks
                    }
                ]
                : []),
            ...(status === TaskCompletionStatusEnum.New || status === TaskCompletionStatusEnum.InProgress
                ? [
                    {
                        text: t('tasks.delete'),
                        group: true,
                        color: theme.palette.error.light,
                        icon: _jsx(Delete, { style: { width: 20, height: 20 } }),
                        to: () => setOpenDeleteConfirmation(true),
                        disabled: isUpdating || !canDeleteTasks
                    }
                ]
                : [])
        ];
    })();
    const handleTaskCardClick = (event) => {
        event.stopPropagation();
        // Disable click if disableClick prop is true (e.g., when shown in ViewTaskDialog)
        if (disableClick) {
            return;
        }
        // Disable click for completed tasks
        if (status === TaskCompletionStatusEnum.Completed) {
            return;
        }
        const interactiveTarget = event.target.closest('[data-prevent-task-card-click="true"]');
        if (interactiveTarget) {
            return;
        }
        if (anchorUsersEl || anchorCollaboratorsEl || anchorMoreOptionEl) {
            return;
        }
        // setIsViewTaskOpen(true);
    };
    return (_jsxs(_Fragment, { children: [_jsxs(Box, { className: 'card__task', sx: { ...styles.taskCard, ...taskCardSx }, onClick: handleTaskCardClick, children: [_jsxs(Box, { className: 'box__top', sx: { ...styles.boxTop }, children: [taskStatus && status !== undefined ? (_jsxs(_Fragment, { children: [(() => {
                                        const statusInfo = getStatusInfo();
                                        return (_jsxs(Box, { display: 'flex', alignItems: 'center', gap: 0.75, children: [_jsx(Box, { sx: {
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: '50%',
                                                        backgroundColor: statusInfo.circleColor,
                                                        flexShrink: 0
                                                    } }), _jsx(Typography, { variant: 'body2', sx: {
                                                        color: statusInfo.textColor,
                                                        fontWeight: 500,
                                                        fontSize: '14px'
                                                    }, children: statusInfo.label })] }));
                                    })(), _jsxs(Box, { display: 'flex', alignItems: 'center', gap: 1, children: [_jsx(Chip, { label: t(getPriorityTranslationKey(priority)), size: 'small', sx: styles.priorityChip }), !hideMenu && (_jsx(IconButton, { color: 'default', size: 'small', onClick: (e) => {
                                                    e.stopPropagation();
                                                    setAnchorMoreOptionEl(e.currentTarget);
                                                }, children: _jsx(Icon, { icon: 'solar:menu-dots-bold', color: theme.palette.text.primary }) }))] })] })) : (_jsxs(_Fragment, { children: [_jsx(Chip, { label: t(getPriorityTranslationKey(priority)), size: 'small', sx: styles.priorityChip }), _jsx(Box, { display: 'flex', alignItems: 'center', gap: 1, children: !hideMenu && (_jsx(IconButton, { color: 'default', size: 'small', onClick: (e) => {
                                                e.stopPropagation();
                                                setAnchorMoreOptionEl(e.currentTarget);
                                            }, children: _jsx(Icon, { icon: 'solar:menu-dots-bold', color: theme.palette.text.primary }) })) })] })), anchorUsersEl && (_jsx(StyledMenu, { anchorEl: anchorUsersEl, open: Boolean(anchorUsersEl), onClose: () => {
                                    setAnchorUsersEl(null);
                                    setUserSearchTerm('');
                                }, actions: userMenu, searchField: _jsx(Box, { sx: {
                                        px: 2,
                                        pb: 1,
                                        pt: 1,
                                        backgroundColor: theme.palette.background.paper,
                                        zIndex: 1,
                                        flexShrink: 0,
                                        position: 'sticky',
                                        top: 0
                                    }, children: _jsx(TextField, { fullWidth: true, placeholder: 'Search users...', variant: 'outlined', size: 'small', value: userSearchTerm, onChange: (e) => {
                                            e.stopPropagation();
                                            setUserSearchTerm(e.target.value);
                                        }, onClick: (e) => e.stopPropagation(), onKeyDown: (e) => e.stopPropagation(), onMouseDown: (e) => e.stopPropagation(), autoComplete: 'off', inputProps: {
                                            onBlur: (e) => {
                                                e.preventDefault();
                                                e.target.focus();
                                            }
                                        } }) }) })), anchorCollaboratorsEl && shouldShowCollaborators() && (_jsx(StyledMenu, { anchorEl: anchorCollaboratorsEl, open: Boolean(anchorCollaboratorsEl), onClose: () => setanchorCollaboratorsEl(null), actions: collaboratorMenu }))] }), _jsx(Typography, { sx: { ...styles.taskHeading }, variant: 'body1', color: 'textPrimary', title: translatedHeading, children: translatedHeading }), _jsxs(Box, { className: 'box__bottom', sx: { ...styles.boxBottom }, children: [showRating && (_jsxs(Box, { className: 'box__item', sx: { ...styles.boxItem, alignItems: 'flex-start' }, children: [_jsx(Box, { flexShrink: 0, display: 'flex', alignItems: 'flex-start', children: taskType === TaskType.People_Activity ? (_jsx(UserTick, { size: '20', color: theme.palette.grey[700] })) : taskType === TaskType.Event ? (_jsx(MagicStar, { size: '20', color: theme.palette.grey[700] })) : (_jsx(Task, { size: '20', color: theme.palette.grey[700] })) }), _jsx(Typography, { variant: 'body2', color: theme.palette.grey[700], sx: { lineHeight: 1.5, wordBreak: 'break-word', width: 'calc(100% - 70px)' }, children: taskType === TaskType.People_Activity
                                            ? getPeopleActivityLabel()
                                            : taskType === TaskType.Event
                                                ? taskData?.eventName || eventOrganizer || 'N/A'
                                                : t('tasks.others') })] })), showPersonIcon && (_jsxs(Box, { className: 'box__item', sx: { ...styles.boxItem, alignItems: 'flex-start' }, children: [_jsx(Box, { flexShrink: 0, display: 'flex', alignItems: 'flex-start', children: _jsx(UserTick, { size: '20', color: theme.palette.grey[700] }) }), _jsx(Typography, { variant: 'body2', color: theme.palette.grey[700], sx: { lineHeight: 1.5, wordBreak: 'break-word', width: 'calc(100% - 70px)' }, children: getPeopleActivityLabel() })] })), _jsxs(Box, { className: 'box__item', sx: { ...styles.boxItem }, children: [_jsx(Calendar, { size: '20', color: theme.palette.text.secondary }), _jsxs(Typography, { variant: 'body2', color: 'textSecondary', sx: { display: 'flex' }, children: [date, _jsx("span", { children: _jsx(ArrowRight, { size: 16, style: { marginTop: 3 } }) }), dayjs(time, ['h:mmA', 'hh:mm A']).format('HH:mm')] })] }), _jsxs(Box, { className: 'box__item', sx: {
                                    ...styles.boxItem,
                                    width: '100%',
                                    borderTop: `1px solid ${theme.palette.divider}`,
                                    justifyContent: 'space-between',
                                    gap: 1
                                    // [theme.breakpoints.up(1500)]: {
                                    //   width: '50%'
                                    // }
                                }, children: [selectedUsers.length > 0 ? (
                                    // Show grouped avatars when users are assigned
                                    _jsxs(Box, { sx: {
                                            display: 'flex',
                                            alignItems: 'center',
                                            mt: '7px',
                                            // mb: '5px',
                                            cursor: isUpdating || userAssignedDisabled || !canUpdateTasks || isCollaborator
                                                ? 'default'
                                                : 'pointer',
                                            overflow: 'visible'
                                        }, "data-prevent-task-card-click": 'true', onClick: (e) => {
                                            if (isUpdating || userAssignedDisabled || !canUpdateTasks || isCollaborator)
                                                return;
                                            e.stopPropagation();
                                            setAnchorUsersEl(e.currentTarget);
                                        }, children: [assignedUsers.slice(0, 3).map((user, idx) => {
                                                const statusInfo = getStatusInfo();
                                                return (_jsx(Avatar, { src: user.profilePictureUrl &&
                                                        user.profilePictureUrl !== '/placeholder.svg?height=24&width=24'
                                                        ? user.profilePictureUrl
                                                        : undefined, alt: `${user.firstName || ''} ${user.lastName || ''}`, sx: {
                                                        width: 32,
                                                        height: 32,
                                                        ml: idx === 0 ? 0 : '-8px',
                                                        border: `2px solid ${theme.palette.background.paper}`,
                                                        fontSize: '12px',
                                                        fontWeight: 500,
                                                        borderRadius: 1.5,
                                                        zIndex: 3 - idx,
                                                        backgroundColor: theme.palette.background.neutral,
                                                        color: theme.palette.action.active
                                                    }, children: getUserInitials(user) }, user.id || idx));
                                            }), assignedUsers.length > 3 &&
                                                (() => {
                                                    const statusInfo = getStatusInfo();
                                                    return (_jsxs(Avatar, { sx: {
                                                            width: 32,
                                                            height: 32,
                                                            ml: '-8px',
                                                            fontSize: '12px',
                                                            borderRadius: 1.5,
                                                            fontWeight: 500,
                                                            border: `2px solid ${theme.palette.background.paper}`,
                                                            backgroundColor: theme.palette.primary.lighter,
                                                            color: theme.palette.primary.dark,
                                                            zIndex: 0
                                                        }, children: ["+", assignedUsers.length - 3] }));
                                                })()] })) : (
                                    // Show "Assign To" button when no one is assigned
                                    _jsx(Button, { variant: 'outlined', 
                                        //color='primary'
                                        size: 'small', endIcon: _jsx(ArrowRight, { size: 18 }), sx: {
                                            ...styles.assignToDropdown,
                                            textTransform: 'none',
                                            fontWeight: 400,
                                            ...assignButtonSx
                                        }, "data-prevent-task-card-click": 'true', onClick: (e) => {
                                            setAnchorUsersEl(e.currentTarget);
                                        }, disabled: isUpdating || userAssignedDisabled || !canUpdateTasks || isCollaborator, children: _jsx(Typography, { noWrap: true, sx: {
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                fontSize: '13px',
                                                maxWidth: { xs: '200px', lg: '100%' }
                                            }, children: t('tasks.assignto') }) })), shouldShowCollaborators() &&
                                        (selectedCollaborators.length > 0 ? (_jsxs(Button, { variant: 'soft', size: 'small', disableRipple: true, onClick: handleCollaboratorsMenuOpen, disabled: isUpdating || !canUpdateTasks || isCollaborator, sx: {
                                                ...styles.collaboratorButton,
                                                minWidth: 'auto',
                                                padding: '6px 10px',
                                                borderRadius: '12px',
                                                color: theme.palette.text.primary,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.8,
                                                '&:hover': {
                                                    backgroundColor: theme.palette.grey[200]
                                                }
                                            }, children: [_jsx(UserOctagon, { size: 18 }), _jsx(Box, { sx: {
                                                        backgroundColor: alpha(theme.palette.grey[500], 0.2),
                                                        borderRadius: '50%',
                                                        width: 20,
                                                        height: 20,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '13px',
                                                        fontWeight: 500,
                                                        color: theme.palette.text.secondary
                                                    }, children: selectedCollaborators.length })] })) : (_jsx(Button, { variant: 'outlined', color: 'primary', size: 'small', sx: {
                                                ...styles.collaboratorButton
                                            }, "data-prevent-task-card-click": 'true', onClick: handleCollaboratorsMenuOpen, disabled: isUpdating || !canUpdateTasks || isCollaborator, children: _jsx(UserOctagon, { size: 18 }) }))), anchorMoreOptionEl && (_jsx(StyledMenu, { anchorEl: anchorMoreOptionEl, open: Boolean(anchorMoreOptionEl), onClose: () => {
                                            setAnchorMoreOptionEl(null);
                                        }, actions: moreMenuOptions }))] })] }), isDrawerOpen && (_jsxs(Drawer, { anchor: 'bottom', open: isDrawerOpen, onClose: () => setIsDrawerOpen(false), PaperProps: {
                            sx: {
                                ...styles.bottomNewTaskDrawer
                            }
                        }, children: [_jsx(Typography, { variant: 'h6', px: '18px', children: t('tasks.editTask') }), _jsx(AddNewTask, { isCreateAllowed: canCreateTasks, edit: true, taskData: taskData, onClose: () => setIsDrawerOpen(false) }, `${taskData?.id}-${taskData?.eventId}`)] })), isEditTaskOpen && (_jsx(AddNewTaskDialog, { isCreateAllowed: canCreateTasks, edit: true, onClose: () => setIsEditTaskOpen(false), open: isEditTaskOpen, taskData: taskData }, `${taskData?.id}-${taskData?.eventId}`)), openDeleteConfirmation && (_jsx(CustomDialog, { open: openDeleteConfirmation, title: t('common.deleteConfirmation'), onClose: () => setOpenDeleteConfirmation(false), actions: _jsx(_Fragment, { children: _jsxs(Box, { sx: centeredRowStack, width: '100%', gap: 1, children: [_jsx(Button, { fullWidth: true, variant: 'outlined', color: 'inherit', onClick: () => setOpenDeleteConfirmation(false), children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: 'error', fullWidth: true, disabled: !canDeleteTasks || isUpdating, onClick: () => {
                                            handleDeleteTask();
                                        }, children: t('actions.deleteNow') })] }) }), children: _jsx(Box, { padding: 3, children: _jsx(Typography, { children: t('common.deleteWarningMessage') }) }) }))] }), _jsx(TaskViewDialog, { open: isViewTaskOpen, onClose: () => setIsViewTaskOpen(false), taskData: taskData })] }));
};
export default TaskCard;
